import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SharedModule } from 'src/app/shared/shared.module';
import { UiModule } from 'src/app/ui/ui.module';
import { TranslateService } from '@ngx-translate/core';
import { InboxConfirmationActionType } from '../../models/inbox.models';

/** Single localStorage key holding a JSON array of skip types (e.g. ["account_update", "share_personal_conversation"]) */
const INBOX_CONFIRMATION_SKIP_KEY = 'inbox_confirmation_skip';

/** Skip-type identifiers for "don't show again". Add new types here when adding new skippable confirmations. */
export const InboxConfirmationSkipType = {
  ACCOUNT_UPDATE: 'account_update',
  SHARE_PERSONAL_CONVERSATION: 'share_personal_conversation',
} as const;
export type InboxConfirmationSkipType = (typeof InboxConfirmationSkipType)[keyof typeof InboxConfirmationSkipType];

@Component({
  selector: 'app-inbox-confirmation-dialog',
  standalone: true,
  imports: [SharedModule, UiModule],
  templateUrl: './inbox-confirmation-dialog.component.html',
  styleUrls: ['./inbox-confirmation-dialog.component.scss']
})
export class InboxConfirmationDialogComponent {

  isLoading = false;

  constructor(
    private dialogRef: MatDialogRef<InboxConfirmationDialogComponent>,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: { actionType: InboxConfirmationActionType; inboxId?: number }
  ) {}

  getIcon(): string | null {
    switch (this.data.actionType) {
      case InboxConfirmationActionType.DELETE:
      case InboxConfirmationActionType.DELETE_COMMENT:
      case InboxConfirmationActionType.DELETE_SIGNATURE:
        return 'trash-cc';
      case InboxConfirmationActionType.DISCONNECT:
        return 'logout-cc';
      default:
        return null;
    }
  }

  getTitle(): string {
    switch (this.data.actionType) {
      case InboxConfirmationActionType.SHARE_ACCOUNT:
        return this.translateService.instant('smart_inbox.confirmation.share_account.title');
      case InboxConfirmationActionType.UNSHARE_ACCOUNT:
        return this.translateService.instant('smart_inbox.confirmation.unshare_account.title');
      case InboxConfirmationActionType.SHARE_PERSONAL_CONVERSATION:
        return this.translateService.instant('smart_inbox.confirmation.share_personal_conversation.title');
      case InboxConfirmationActionType.DISCONNECT:
        return this.translateService.instant('smart_inbox.confirmation.disconnect_inbox.title');
      case InboxConfirmationActionType.DELETE_COMMENT:
        return this.translateService.instant('smart_inbox.confirmation.delete_comment.title');
      case InboxConfirmationActionType.DELETE_SIGNATURE:
        return this.translateService.instant('smart_inbox.confirmation.delete_signature.title');
      default:
        return '';
    }
  }

  getDescription(): string {
    switch (this.data.actionType) {
      case InboxConfirmationActionType.SHARE_ACCOUNT:
        return this.translateService.instant('smart_inbox.confirmation.share_account.description');
      case InboxConfirmationActionType.UNSHARE_ACCOUNT:
        return this.translateService.instant('smart_inbox.confirmation.unshare_account.description');
      case InboxConfirmationActionType.SHARE_PERSONAL_CONVERSATION:
        return this.translateService.instant('smart_inbox.confirmation.share_personal_conversation.description');
      case InboxConfirmationActionType.DISCONNECT:
        return this.translateService.instant('smart_inbox.confirmation.disconnect_inbox.description');
      case InboxConfirmationActionType.DELETE_COMMENT:
        return this.translateService.instant('smart_inbox.confirmation.delete_comment.description');
      case InboxConfirmationActionType.DELETE_SIGNATURE:
        return this.translateService.instant('smart_inbox.confirmation.delete_signature.description');
      default:
        return '';
    }
  }

  confirm() {
    switch (this.data.actionType) {
      case InboxConfirmationActionType.SHARE_ACCOUNT:
      case InboxConfirmationActionType.UNSHARE_ACCOUNT:
      case InboxConfirmationActionType.SHARE_PERSONAL_CONVERSATION:
      case InboxConfirmationActionType.DISCONNECT:
      case InboxConfirmationActionType.DELETE_COMMENT:
      case InboxConfirmationActionType.DELETE_SIGNATURE:
        this.dialogRef.close(true);
        break;
      default:
        break;
    }
  }

  cancel() {
    if (this.isLoading) return; // Prevent canceling while loading
    this.dialogRef.close(false);
  }

  /** True when this dialog supports "don't show again" (account type change or share personal conversation). */
  get showDontShowAgain(): boolean {
    return (
      this.data.actionType === InboxConfirmationActionType.SHARE_PERSONAL_CONVERSATION
    );
  }

  /** Same as confirm, but persist "don't show again" in localStorage so this confirmation is skipped next time. */
  confirmAndDontShowAgain(): void {
    const skipType = this.getSkipTypeForAction(this.data.actionType);
    if (skipType) {
      InboxConfirmationDialogComponent.setSkipConfirmation(skipType);
    }
    this.confirm();
  }

  /** Returns the skip type for this action type, or null if not skippable. */
  private getSkipTypeForAction(actionType: InboxConfirmationActionType): string | null {
    switch (actionType) {
      case InboxConfirmationActionType.SHARE_ACCOUNT:
      case InboxConfirmationActionType.UNSHARE_ACCOUNT:
        return InboxConfirmationSkipType.ACCOUNT_UPDATE;
      case InboxConfirmationActionType.SHARE_PERSONAL_CONVERSATION:
        return InboxConfirmationSkipType.SHARE_PERSONAL_CONVERSATION;
      default:
        return null;
    }
  }

  /** Check if a given confirmation type should be skipped (caller should not open this dialog). */
  static shouldSkipConfirmation(skipType: string): boolean {
    try {
      const raw = localStorage.getItem(INBOX_CONFIRMATION_SKIP_KEY);
      if (!raw) return false;
      const arr = JSON.parse(raw) as unknown;
      return Array.isArray(arr) && arr.includes(skipType);
    } catch {
      return false;
    }
  }

  /** Add a skip type to the stored list (used when user clicks "Don't show again"). */
  static setSkipConfirmation(skipType: string): void {
    try {
      const raw = localStorage.getItem(INBOX_CONFIRMATION_SKIP_KEY);
      const arr: string[] = raw ? (JSON.parse(raw) as string[]) : [];
      if (!arr.includes(skipType)) {
        arr.push(skipType);
        localStorage.setItem(INBOX_CONFIRMATION_SKIP_KEY, JSON.stringify(arr));
      }
    } catch (_) {}
  }
}
